import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaChevronRight } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Projection.css';

const BASE_URL = 'https://api.rside.app';

const SETUP_DEFAULT = {
  property_name: '',
  gross_monthly_rent: '',
  vacancy_rate: '5',
  property_tax_annual: '',
  insurance_annual: '',
  repairs_maintenance_annual: '',
  other_deduction_percent: '0',
  mortgage_monthly: '',
  hoa_monthly: '',
  purchase_price: '',
  renovation_cost: '',
  down_payment: '',
  water_on: false, water_monthly: '',
  trash_on: false, trash_monthly: '',
  gas_on: false,  gas_monthly: '',
  electricity_on: false, electricity_monthly: '',
};

function calcProjection(s) {
  const grossMonthly    = parseFloat(s.gross_monthly_rent) || 0;
  const vacRate         = parseFloat(s.vacancy_rate)       || 5;
  const propTax         = parseFloat(s.property_tax_annual)        || 0;
  const insurance       = parseFloat(s.insurance_annual)           || 0;
  const repInput        = s.repairs_maintenance_annual;
  const repairsFromRule = repInput === '' || repInput == null;
  const repairs         = repairsFromRule
    ? (grossMonthly > 0 ? grossMonthly * 1.5 : 0)
    : (parseFloat(repInput) || 0);
  const mortgageMonthly = parseFloat(s.mortgage_monthly)  || 0;
  const hoaMonthly      = parseFloat(s.hoa_monthly)       || 0;
  const otherPct        = parseFloat(s.other_deduction_percent) || 0;
  const waterMonthly    = s.water_on      ? (parseFloat(s.water_monthly)       || 0) : 0;
  const trashMonthly    = s.trash_on      ? (parseFloat(s.trash_monthly)       || 0) : 0;
  const gasMonthly      = s.gas_on        ? (parseFloat(s.gas_monthly)         || 0) : 0;
  const elecMonthly     = s.electricity_on? (parseFloat(s.electricity_monthly) || 0) : 0;
  const purchasePrice   = parseFloat(s.purchase_price)   || 0;
  const renovationCost  = parseFloat(s.renovation_cost)  || 0;
  const downPayment     = parseFloat(s.down_payment)     || 0;

  const grossAnnual        = grossMonthly * 12;
  const vacAdj             = grossAnnual * (vacRate / 100);
  const egi                = grossAnnual - vacAdj;
  const utilitiesAnnual    = (waterMonthly + trashMonthly + gasMonthly + elecMonthly) * 12;
  const mortgageAnnual     = mortgageMonthly * 12;
  const hoaAnnual          = hoaMonthly * 12;
  const otherDeductAnnual  = egi * (otherPct / 100);
  const operatingExpenses  = propTax + insurance + repairs + utilitiesAnnual + hoaAnnual + otherDeductAnnual;
  const totalExpenses      = operatingExpenses + mortgageAnnual;
  const noi                = egi - operatingExpenses;
  const cashFlow           = egi - totalExpenses;
  const totalCost          = purchasePrice + renovationCost;
  const cashBase           = downPayment > 0 ? downPayment : purchasePrice;
  const cashInvested       = cashBase + renovationCost;
  const capRate            = totalCost   > 0 ? (noi      / totalCost    * 100) : null;
  const coc                = cashInvested> 0 ? (cashFlow / cashInvested * 100) : null;

  return {
    grossMonthly, grossAnnual, vacRate, vacAdj, egi,
    propTax, insurance, repairs, repairsFromRule,
    utilitiesAnnual, waterMonthly, trashMonthly, gasMonthly, elecMonthly,
    mortgageMonthly, mortgageAnnual, hoaMonthly, hoaAnnual,
    otherPct, otherDeductAnnual,
    operatingExpenses, totalExpenses, noi, cashFlow,
    purchasePrice, renovationCost, downPayment, totalCost, cashInvested,
    capRate, coc,
  };
}

const UTILS = [
  { key: 'water',       label: 'Water & Sewer', icon: '💧' },
  { key: 'trash',       label: 'Trash',         icon: '🗑️' },
  { key: 'gas',         label: 'Gas',           icon: '🔥' },
  { key: 'electricity', label: 'Electricity',   icon: '⚡' },
];

/* Defined outside component so React never unmounts/remounts the input on re-render */
function NumField({ label, prefix, suffix, placeholder = '0.00', value, onChange }) {
  return (
    <div className="proj-field-row">
      <span className="proj-field-label">{label}</span>
      <div className="proj-field-input-wrap">
        {prefix && <span className="proj-field-prefix">{prefix}</span>}
        <input
          type="number"
          className="proj-field-input"
          placeholder={placeholder}
          value={value ?? ''}
          onChange={onChange}
        />
        {suffix && <span className="proj-field-suffix">{suffix}</span>}
      </div>
    </div>
  );
}

function Projection() {
  const navigate = useNavigate();
  const [initials, setInitials]     = useState('');
  const [step, setStep]             = useState('select');
  const [allProps, setAllProps]     = useState([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [selected, setSelected]     = useState(null);
  const [setup, setSetup]           = useState({ ...SETUP_DEFAULT });
  const [fetching, setFetching]     = useState(false);
  const [financialsFrom, setFinancialsFrom] = useState(new Set());
  const [results, setResults]       = useState(null);
  const [localData, setLocalData]   = useState(null);
  const [infoPopup, setInfoPopup]   = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return navigate('/login');
      try {
        const res  = await fetch(`${BASE_URL}/PropertyManagerName?email=${encodeURIComponent(u.email)}`);
        const data = await res.json();
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        const propRes = await fetch(
          `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(data.Full_Name || '')}&Email=${encodeURIComponent(u.email)}`
        );
        if (propRes.ok) {
          const propData = await propRes.json();
          setAllProps((propData.Properties || []).map(p => ({
            id: p.Property_ID || p.Door_id,
            name: p.Property_Name || 'Unknown',
            address: p.Street_Address || '',
            units: p.Num_Of_Units || 1,
          })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      } catch (err) { console.error(err); }
      finally { setPropsLoading(false); }
    });
    return unsub;
  }, [navigate]);

  const selectProperty = async (prop) => {
    setSelected(prop);
    setSetup({ ...SETUP_DEFAULT });
    setResults(null);
    setLocalData(null);
    setFinancialsFrom(new Set());
    setStep('setup');

    if (prop?.id) {
      setFetching(true);
      try {
        const fr = await fetch(`${BASE_URL}/PropertyFinancials?property_id=${prop.id}`);
        if (fr.ok) {
          const fd  = await fr.json();
          const fin = fd.financials?.[0] || null;
          if (fin) {
            const fromKeys = new Set();
            setSetup(s => {
              const next = { ...s };
              if (fin.Tax)             { next.property_tax_annual  = String(fin.Tax);             fromKeys.add('property_tax_annual'); }
              const ins = fin.Insurance || fin.Insurance_Annual || fin.insurance_annual;
              if (ins)                 { next.insurance_annual      = String(ins);                 fromKeys.add('insurance_annual'); }
              if (fin.Mortgage_Payment){ next.mortgage_monthly       = String(fin.Mortgage_Payment); fromKeys.add('mortgage_monthly'); }
              if (fin.HOA)             { next.hoa_monthly            = String(fin.HOA);             fromKeys.add('hoa_monthly'); }
              if (fin.Purchase_Price)  { next.purchase_price         = String(fin.Purchase_Price);  fromKeys.add('purchase_price'); }
              if (fin.Renov_Cost)      { next.renovation_cost        = String(fin.Renov_Cost);      fromKeys.add('renovation_cost'); }
              if (fin.Down_Payment)    { next.down_payment           = String(fin.Down_Payment);    fromKeys.add('down_payment'); }
              return next;
            });
            setFinancialsFrom(fromKeys);
          }
        }
      } catch (err) { console.error(err); }
      finally { setFetching(false); }
    }
  };

  const runProjection = () => {
    const r = calcProjection(setup);
    setResults({ ...r, _setup: { ...setup }, _fromFinancials: [...financialsFrom] });
    setLocalData({
      gross_monthly_rent:         String(setup.gross_monthly_rent || '0'),
      vacancy_rate:               String(setup.vacancy_rate || '5'),
      repairs_maintenance_annual: String(r.repairs),
      other_deduction_percent:    String(setup.other_deduction_percent || '0'),
    });
    setStep('results');
  };

  const compute = (r, ld) => {
    if (!r || !ld) return null;
    return calcProjection({
      ...r._setup,
      gross_monthly_rent:         ld.gross_monthly_rent,
      vacancy_rate:               ld.vacancy_rate,
      repairs_maintenance_annual: ld.repairs_maintenance_annual,
      other_deduction_percent:    ld.other_deduction_percent,
    });
  };

  const fmtN = (v) => Math.round(Math.abs(Number(v) || 0)).toLocaleString('en-US');
  const fmtP = (v) => v == null ? 'N/A' : `${Number(v).toFixed(2)}%`;
  const setS  = (key, val) => setSetup(s => ({ ...s, [key]: val }));

  /* ── RESULTS RENDERER ── */
  const renderResults = () => {
    const d = compute(results, localData);
    if (!d) return null;
    const fromFin = new Set(results._fromFinancials || []);
    const FB = () => <span className="proj-from-db">From Financials</span>;
    const resultName = selected?.name || results._setup?.property_name || '';

    return (
      <div className="proj-results-body">

        {/* 3 summary cards */}
        <div className="proj-summary-cards">
          <div className="proj-summary-card proj-card-green">
            <div className="proj-card-label">Income</div>
            <div className="proj-card-main">${fmtN(d.egi)}</div>
            <div className="proj-card-sub">${fmtN(d.egi / 12)}/mo</div>
          </div>
          <div className="proj-summary-card proj-card-red">
            <div className="proj-card-label">Expenses</div>
            <div className="proj-card-main proj-card-main-red">${fmtN(d.totalExpenses)}</div>
            <div className="proj-card-sub proj-card-sub-red">${fmtN(d.totalExpenses / 12)}/mo</div>
          </div>
          <div className={`proj-summary-card ${d.cashFlow >= 0 ? 'proj-card-green' : 'proj-card-red'}`}>
            <div className="proj-card-label">Cash Flow</div>
            <div className={`proj-card-main ${d.cashFlow < 0 ? 'proj-card-main-red' : ''}`}>
              {d.cashFlow < 0 ? '-' : ''}${fmtN(Math.abs(d.cashFlow))}
            </div>
            <div className={`proj-card-sub ${d.cashFlow < 0 ? 'proj-card-sub-red' : ''}`}>
              {d.cashFlow < 0 ? '-' : ''}${fmtN(Math.abs(d.cashFlow / 12))}/mo
            </div>
          </div>
        </div>

        <div className="proj-results-grid">
          <div className="proj-results-col">

            {/* Income */}
            <div className="proj-section-card">
              <div className="proj-section-card-header">
                <span className="proj-section-card-title">Income</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">Gross Monthly Rent</span>
                <div className="proj-result-edit">
                  <span className="proj-edit-prefix">$</span>
                  <input type="number" className="proj-edit-input"
                    value={localData.gross_monthly_rent}
                    onChange={e => setLocalData(l => ({ ...l, gross_monthly_rent: e.target.value }))} />
                </div>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">Gross Annual Rent</span>
                <span className="proj-result-val proj-val-green">${fmtN(d.grossAnnual)}</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">Vacancy Adjustment</span>
                <div className="proj-result-edit">
                  <input type="number" className="proj-edit-input"
                    value={localData.vacancy_rate}
                    onChange={e => setLocalData(l => ({ ...l, vacancy_rate: e.target.value }))} />
                  <span className="proj-edit-suffix">%</span>
                </div>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">Less Vacancy</span>
                <span className="proj-result-val proj-val-orange">- ${fmtN(d.vacAdj)}</span>
              </div>
              <div className="proj-result-row proj-result-total-row">
                <span className="proj-result-label proj-result-total-label">Effective Gross Income</span>
                <span className="proj-result-val proj-val-green proj-result-total-val">${fmtN(d.egi)}</span>
              </div>
            </div>

            {/* Annual Expenses */}
            <div className="proj-section-card">
              <div className="proj-section-card-header">
                <span className="proj-section-card-title">Annual Expenses</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">
                  Property Tax
                  {fromFin.has('property_tax_annual') && <FB />}
                </span>
                <span className="proj-result-val">${fmtN(d.propTax)}</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">
                  Insurance
                  {fromFin.has('insurance_annual') && <FB />}
                </span>
                <span className="proj-result-val">${fmtN(d.insurance)}</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">
                  Repairs &amp; Maintenance
                  {d.repairsFromRule && <span className="proj-from-db">Auto</span>}
                </span>
                <div className="proj-result-edit">
                  <span className="proj-edit-prefix">$</span>
                  <input type="number" className="proj-edit-input" placeholder="Annual"
                    value={localData.repairs_maintenance_annual}
                    onChange={e => setLocalData(l => ({ ...l, repairs_maintenance_annual: e.target.value }))} />
                </div>
              </div>
              {d.utilitiesAnnual > 0 && (
                <div className="proj-result-row">
                  <span className="proj-result-label">Utilities</span>
                  <span className="proj-result-val proj-val-red">${fmtN(d.utilitiesAnnual)}</span>
                </div>
              )}
            </div>

          </div>

          <div className="proj-results-col">

            {/* Monthly Expenses */}
            <div className="proj-section-card">
              <div className="proj-section-card-header">
                <span className="proj-section-card-title">Monthly Expenses</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">
                  Mortgage
                  {fromFin.has('mortgage_monthly') && <FB />}
                </span>
                <span className="proj-result-val">${fmtN(d.mortgageMonthly)}/mo</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">
                  HOA
                  {fromFin.has('hoa_monthly') && <FB />}
                </span>
                <span className="proj-result-val">${fmtN(d.hoaMonthly)}/mo</span>
              </div>
              <div className="proj-result-row">
                <span className="proj-result-label">Other Deduction</span>
                <div className="proj-result-edit">
                  <input type="number" className="proj-edit-input"
                    value={localData.other_deduction_percent}
                    onChange={e => setLocalData(l => ({ ...l, other_deduction_percent: e.target.value }))} />
                  <span className="proj-edit-suffix">%</span>
                </div>
              </div>
            </div>

            {/* Metric rows */}
            <div className="proj-metric-row" onClick={() => setInfoPopup('expenses')}>
              <span className="proj-metric-label">Total Annual Expenses</span>
              <span className="proj-metric-val proj-val-red">${fmtN(d.totalExpenses)}</span>
            </div>
            <div className="proj-metric-row" onClick={() => setInfoPopup('noi')}>
              <span className="proj-metric-label">Net Operating Income</span>
              <span className={`proj-metric-val ${d.noi >= 0 ? 'proj-val-green' : 'proj-val-red'}`}>
                {d.noi < 0 ? '-' : ''}${fmtN(Math.abs(d.noi))}
              </span>
            </div>
            <div className="proj-metric-row" onClick={() => setInfoPopup('cap')}>
              <span className="proj-metric-label">Cap Rate</span>
              <span className={`proj-metric-val ${(d.capRate ?? 0) >= 0 ? 'proj-val-green' : 'proj-val-red'}`}>
                {fmtP(d.capRate)}
              </span>
            </div>
            <div className="proj-metric-row" onClick={() => setInfoPopup('coc')}>
              <span className="proj-metric-label">Cash-on-Cash Return</span>
              <span className={`proj-metric-val ${(d.coc ?? 0) >= 0 ? 'proj-val-green' : 'proj-val-red'}`}>
                {fmtP(d.coc)}
              </span>
            </div>

          </div>
        </div>

        <div className="proj-hint">Click any metric row to see the breakdown. Editable fields recalculate instantly.</div>

        {infoPopup && (
          <div className="proj-info-overlay" onClick={() => setInfoPopup(null)}>
            <div className="proj-info-modal" onClick={e => e.stopPropagation()}>
              <div className="proj-info-header">
                <span className="proj-info-title">
                  {infoPopup === 'expenses' && 'Total Annual Expenses'}
                  {infoPopup === 'noi'      && 'Net Operating Income'}
                  {infoPopup === 'cap'      && 'Cap Rate'}
                  {infoPopup === 'coc'      && 'Cash-on-Cash Return'}
                </span>
                <button className="proj-info-close" onClick={() => setInfoPopup(null)}>✕</button>
              </div>

              {infoPopup === 'expenses' && <>
                <div className="proj-info-section-label">Operating Expenses</div>
                <div className="proj-info-row"><span>Property Tax</span><span className="proj-val-red">${fmtN(d.propTax)}</span></div>
                <div className="proj-info-row"><span>Insurance</span><span className="proj-val-red">${fmtN(d.insurance)}</span></div>
                <div className="proj-info-row"><span>Repairs &amp; Maintenance</span><span className="proj-val-red">${fmtN(d.repairs)}</span></div>
                <div className="proj-info-row"><span>Utilities</span><span className="proj-val-red">${fmtN(d.utilitiesAnnual)}</span></div>
                <div className="proj-info-row"><span>HOA (Annual)</span><span className="proj-val-red">${fmtN(d.hoaAnnual)}</span></div>
                <div className="proj-info-row"><span>Other Deduction</span><span className="proj-val-red">${fmtN(d.otherDeductAnnual)}</span></div>
                <div className="proj-info-row proj-info-subtotal"><span>Operating Expenses</span><span className="proj-val-red">${fmtN(d.operatingExpenses)}</span></div>
                <div className="proj-info-section-label" style={{ marginTop: 12 }}>Debt Service</div>
                <div className="proj-info-row"><span>Mortgage (Annual)</span><span className="proj-val-red">${fmtN(d.mortgageAnnual)}</span></div>
                <div className="proj-info-row proj-info-subtotal">
                  <span><strong>Total Annual Expenses</strong></span>
                  <span className="proj-val-red" style={{ fontWeight: 700 }}>${fmtN(d.totalExpenses)}</span>
                </div>
                <div className="proj-info-note">Total expenses include operating costs (used for NOI) plus mortgage payments. HOA is an operating expense; mortgage is debt service.</div>
              </>}

              {infoPopup === 'noi' && <>
                <div className="proj-info-row"><span>Effective Gross Income</span><span className="proj-val-green">${fmtN(d.egi)}</span></div>
                <div className="proj-info-row"><span>Property Tax</span><span className="proj-val-red">- ${fmtN(d.propTax)}</span></div>
                <div className="proj-info-row"><span>Insurance</span><span className="proj-val-red">- ${fmtN(d.insurance)}</span></div>
                <div className="proj-info-row"><span>Repairs &amp; Maintenance</span><span className="proj-val-red">- ${fmtN(d.repairs)}</span></div>
                <div className="proj-info-row"><span>Utilities</span><span className="proj-val-red">- ${fmtN(d.utilitiesAnnual)}</span></div>
                <div className="proj-info-row"><span>HOA (Annual)</span><span className="proj-val-red">- ${fmtN(d.hoaAnnual)}</span></div>
                <div className="proj-info-row"><span>Other Deduction</span><span className="proj-val-red">- ${fmtN(d.otherDeductAnnual)}</span></div>
                <div className="proj-info-row proj-info-subtotal">
                  <span><strong>Net Operating Income</strong></span>
                  <span className={d.noi >= 0 ? 'proj-val-green' : 'proj-val-red'} style={{ fontWeight: 700 }}>
                    {d.noi < 0 ? '-' : ''}${fmtN(Math.abs(d.noi))}
                  </span>
                </div>
                <div className="proj-info-note">NOI measures the property's profitability from operations before financing costs like mortgage payments.</div>
              </>}

              {infoPopup === 'cap' && <>
                <div className="proj-info-row"><span>Net Operating Income</span><span className={d.noi >= 0 ? 'proj-val-green' : 'proj-val-red'}>{d.noi < 0 ? '-' : ''}${fmtN(Math.abs(d.noi))}</span></div>
                <div className="proj-info-divider">÷ Total Property Cost</div>
                <div className="proj-info-row"><span>Purchase Price</span><span>${fmtN(d.purchasePrice)}</span></div>
                <div className="proj-info-row"><span>Renovation &amp; Fees</span><span>${fmtN(d.renovationCost)}</span></div>
                <div className="proj-info-row proj-info-subtotal"><span>Total Cost</span><span>${fmtN(d.totalCost)}</span></div>
                <div className="proj-info-row proj-info-subtotal">
                  <span><strong>Cap Rate</strong></span>
                  <span className={(d.capRate ?? 0) >= 0 ? 'proj-val-green' : 'proj-val-red'} style={{ fontWeight: 700 }}>{fmtP(d.capRate)}</span>
                </div>
                <div className="proj-info-note">Cap Rate measures the expected annual return based on the property's total cost, independent of how it was financed. A higher cap rate indicates a higher potential return.</div>
              </>}

              {infoPopup === 'coc' && <>
                <div className="proj-info-row"><span>Effective Gross Income</span><span className="proj-val-green">${fmtN(d.egi)}</span></div>
                <div className="proj-info-row"><span>Total Expenses</span><span className="proj-val-red">- ${fmtN(d.totalExpenses)}</span></div>
                <div className="proj-info-row proj-info-subtotal"><span>Annual Cash Flow</span><span className={d.cashFlow >= 0 ? 'proj-val-green' : 'proj-val-red'}>{d.cashFlow < 0 ? '-' : ''}${fmtN(Math.abs(d.cashFlow))}</span></div>
                <div className="proj-info-divider">÷ Cash Invested</div>
                <div className="proj-info-row"><span>Down Payment{d.downPayment === 0 ? ' (used purchase price)' : ''}</span><span>${fmtN(d.downPayment > 0 ? d.downPayment : d.purchasePrice)}</span></div>
                <div className="proj-info-row"><span>Renovation &amp; Fees</span><span>${fmtN(d.renovationCost)}</span></div>
                <div className="proj-info-row proj-info-subtotal"><span>Cash Invested</span><span>${fmtN(d.cashInvested)}</span></div>
                <div className="proj-info-row proj-info-subtotal">
                  <span><strong>Cash-on-Cash Return</strong></span>
                  <span className={(d.coc ?? 0) >= 0 ? 'proj-val-green' : 'proj-val-red'} style={{ fontWeight: 700 }}>{fmtP(d.coc)}</span>
                </div>
                <div className="proj-info-note">Cash-on-Cash Return measures the annual return on the actual cash you invested, including mortgage and all expenses. This reflects your real return after financing.</div>
              </>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const isNewProp = !selected?.id;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Run Projection" />
        <div className="proj-container">

          {/* Step header */}
          <div className="proj-step-header">
            {step !== 'select' && (
              <button className="proj-back-btn" onClick={() => {
                if (step === 'setup') setStep('select');
                else { setStep('setup'); setInfoPopup(null); }
              }}>
                ‹ Back
              </button>
            )}
            <div>
              <h1 className="proj-step-title">
                {step === 'select'                  && 'Select a Property'}
                {step === 'setup'  && !isNewProp    && 'Projection Setup'}
                {step === 'setup'  && isNewProp     && 'New Property Analysis'}
                {step === 'results'                 && 'Annual Projection'}
              </h1>
              {(step === 'setup' || step === 'results') && (selected?.name || setup.property_name || results?._setup?.property_name) && (
                <div className="proj-step-subtitle">
                  {selected?.name || setup.property_name || results?._setup?.property_name}
                </div>
              )}
            </div>
          </div>

          {/* ── STEP 1: SELECT ── */}
          {step === 'select' && (
            <div className="proj-select-body">
              <div className="proj-prop-card proj-prop-new" onClick={() => selectProperty(null)}>
                <div className="proj-prop-new-icon">+</div>
                <div className="proj-prop-text">
                  <div className="proj-prop-name">Analyze a New Property</div>
                  <div className="proj-prop-addr">Run a projection with your own numbers</div>
                </div>
                <FaChevronRight className="proj-prop-chevron" />
              </div>

              {propsLoading ? (
                <div className="proj-loading">Loading your properties…</div>
              ) : allProps.length === 0 ? (
                <div className="proj-loading">No properties found</div>
              ) : allProps.map((p, i) => (
                <div key={i} className="proj-prop-card" onClick={() => selectProperty(p)}>
                  <div className="proj-prop-text">
                    <div className="proj-prop-name">{p.name}</div>
                    <div className="proj-prop-addr">{p.address}</div>
                    {p.units > 1 && <div className="proj-prop-units">{p.units} units</div>}
                  </div>
                  <FaChevronRight className="proj-prop-chevron" />
                </div>
              ))}
            </div>
          )}

          {/* ── STEP 2a: SETUP — existing property ── */}
          {step === 'setup' && !isNewProp && (
            <div className="proj-setup-body">
              {fetching && <div className="proj-fetching">Loading property data…</div>}

              {(parseFloat(setup.purchase_price) > 0 || parseFloat(setup.renovation_cost) > 0) && (
                <div className="proj-invest-card">
                  <div className="proj-invest-title">Investment Breakdown</div>
                  <div className="proj-invest-row">
                    <span>Purchase Price</span>
                    <span>${fmtN(setup.purchase_price)}</span>
                  </div>
                  {parseFloat(setup.renovation_cost) > 0 && (
                    <div className="proj-invest-row">
                      <span>Renovation &amp; Other Fees</span>
                      <span>${fmtN(setup.renovation_cost)}</span>
                    </div>
                  )}
                  <div className="proj-invest-row proj-invest-total">
                    <span>Total Cost</span>
                    <span>${fmtN((parseFloat(setup.purchase_price) || 0) + (parseFloat(setup.renovation_cost) || 0))}</span>
                  </div>
                </div>
              )}

              <div className="proj-utilities-heading">Which utilities does the landlord cover?</div>
              <div className="proj-utilities-grid">
                {UTILS.map(u => (
                  <div key={u.key} className={`proj-utility-row${setup[`${u.key}_on`] ? ' proj-utility-on' : ''}`}>
                    <div className="proj-utility-top">
                      <div className="proj-utility-left">
                        <span className="proj-utility-icon">{u.icon}</span>
                        <span className="proj-utility-label">{u.label}</span>
                      </div>
                      <div
                        className={`proj-toggle${setup[`${u.key}_on`] ? ' proj-toggle-on' : ''}`}
                        onClick={() => setS(`${u.key}_on`, !setup[`${u.key}_on`])}
                      >
                        <div className="proj-toggle-thumb" />
                      </div>
                    </div>
                    {setup[`${u.key}_on`] && (
                      <div className="proj-utility-amount">
                        <span className="proj-utility-dollar">$</span>
                        <input
                          type="number"
                          className="proj-utility-input"
                          placeholder="0.00"
                          value={setup[`${u.key}_monthly`]}
                          onChange={e => setS(`${u.key}_monthly`, e.target.value)}
                        />
                        <span className="proj-utility-per">/ month</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="proj-ai-card">
                <div className="proj-ai-icon">✦</div>
                <div className="proj-ai-text">
                  <div className="proj-ai-title">Smart Estimation</div>
                  <div className="proj-ai-sub">Get smart expense estimates based on your property type and location.</div>
                </div>
                <div className="proj-ai-lock">🔒</div>
              </div>

              <button className="proj-run-btn" onClick={runProjection}>Run Projection</button>
            </div>
          )}

          {/* ── STEP 2b: SETUP — new property analysis ── */}
          {step === 'setup' && isNewProp && (
            <div className="proj-new-prop-body">

              {/* Property Info — full width */}
              <div className="proj-setup-section">
                <div className="proj-setup-section-title">Property Info</div>
                <div className="proj-name-field">
                  <div className="proj-name-label">Property Name</div>
                  <input
                    type="text"
                    className="proj-name-input"
                    placeholder="e.g. 123 Main St"
                    value={setup.property_name}
                    onChange={e => setS('property_name', e.target.value)}
                  />
                </div>
              </div>

              {/* 2-column grid — matches rest of site on desktop */}
              <div className="proj-new-prop-grid">

                {/* Left: Purchase Details + Income */}
                <div className="proj-setup-col">
                  <div className="proj-setup-section">
                    <div className="proj-setup-section-title">Purchase Details</div>
                    <NumField label="Purchase Price"          prefix="$" value={setup.purchase_price}  onChange={e => setS('purchase_price',  e.target.value)} />
                    <NumField label="Renovation & Other Fees" prefix="$" value={setup.renovation_cost} onChange={e => setS('renovation_cost', e.target.value)} />
                    <NumField label="Down Payment"            prefix="$" value={setup.down_payment}    onChange={e => setS('down_payment',    e.target.value)} />
                  </div>
                  <div className="proj-setup-section">
                    <div className="proj-setup-section-title">Income</div>
                    <NumField label="Expected Monthly Rent" prefix="$" value={setup.gross_monthly_rent} onChange={e => setS('gross_monthly_rent', e.target.value)} />
                  </div>
                </div>

                {/* Right: Annual Expenses + Monthly Expenses + Utilities */}
                <div className="proj-setup-col">
                  <div className="proj-setup-section">
                    <div className="proj-setup-section-title">Annual Expenses</div>
                    <NumField label="Property Tax" prefix="$" placeholder="Annual" value={setup.property_tax_annual} onChange={e => setS('property_tax_annual', e.target.value)} />
                    <NumField label="Insurance"    prefix="$" placeholder="Annual" value={setup.insurance_annual}    onChange={e => setS('insurance_annual',    e.target.value)} />
                  </div>
                  <div className="proj-setup-section">
                    <div className="proj-setup-section-title">Monthly Expenses</div>
                    <NumField label="Mortgage" prefix="$" suffix="/ mo" value={setup.mortgage_monthly} onChange={e => setS('mortgage_monthly', e.target.value)} />
                    <NumField label="HOA"      prefix="$" suffix="/ mo" value={setup.hoa_monthly}      onChange={e => setS('hoa_monthly',      e.target.value)} />
                  </div>
                  <div className="proj-setup-section">
                    <div className="proj-setup-section-title">Landlord-Covered Utilities</div>
                    {UTILS.map((u, i) => (
                      <div key={u.key} className={`proj-util-inner-row${i < UTILS.length - 1 ? ' proj-util-inner-sep' : ''}`}>
                        <div className="proj-util-inner-top">
                          <div className="proj-utility-left">
                            <span className="proj-utility-icon">{u.icon}</span>
                            <span className="proj-utility-label">{u.label}</span>
                          </div>
                          <div
                            className={`proj-toggle${setup[`${u.key}_on`] ? ' proj-toggle-on' : ''}`}
                            onClick={() => setS(`${u.key}_on`, !setup[`${u.key}_on`])}
                          >
                            <div className="proj-toggle-thumb" />
                          </div>
                        </div>
                        {setup[`${u.key}_on`] && (
                          <div className="proj-utility-amount">
                            <span className="proj-utility-dollar">$</span>
                            <input
                              type="number"
                              className="proj-utility-input"
                              placeholder="0.00"
                              value={setup[`${u.key}_monthly`]}
                              onChange={e => setS(`${u.key}_monthly`, e.target.value)}
                            />
                            <span className="proj-utility-per">/ month</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

              </div>{/* end 2-col grid */}

              <button className="proj-run-btn" onClick={runProjection}>Run Projection</button>
            </div>
          )}

          {/* ── STEP 3: RESULTS ── */}
          {step === 'results' && results && localData && renderResults()}

        </div>
      </div>
    </div>
  );
}

export default Projection;
